/* eslint-disable */
import React, { useEffect, useState, useCallback } from "react";
import { Card, Button, Tooltip, Tag, Select, Divider, Descriptions, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faSave, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import usePutAdapter from "../../../../customHooks/PUT_requests/usePutAdapter";
import { RootState } from "../../../../app/store";
import { IconsInterface, ConditionDef, RuleDefinition, RuleExpr } from "../../../../interfaces/IconDefinitions";
import { AdapterInterface } from "../../../../interfaces/Adapter.interface";

// ICONS
import elDown from "../../../../assets/icons/elevatorStatus/el-down.svg";
import elUp from "../../../../assets/icons/elevatorStatus/el-up.svg";
import elClose from "../../../../assets/icons/elevatorSingle/el-close.svg";
import elOpen from "../../../../assets/icons/elevatorSingle/el-open.svg";
import elFrontCloseRearClose from "../../../../assets/icons/elevatorDouble/el-front-close-rear-close.svg";
import elFrontCloseRearOpen from "../../../../assets/icons/elevatorDouble/el-front-close-rear-open.svg";
import elFrontOpenRearClose from "../../../../assets/icons/elevatorDouble/el-front-open-rear-close.svg";
import elFrontOpenRearOpen from "../../../../assets/icons/elevatorDouble/el-front-open-rear-open.svg";

const { Text } = Typography;

function OrBlock({ children, theme }: { children: React.ReactNode; theme: string }) {
	return (
		<div
			style={{
				borderRadius: 12,
				padding: 10,
				marginTop: 10,
			}}
			className={`generalStyles__flex generalStyles__box__${theme}`}
		>
			<div className='generalStyles__width100'>{children}</div>
		</div>
	);
}

function AndBox({ children }: { children: React.ReactNode }) {
	return <div>{children}</div>;
}

function ConditionTooltipContent({
	conditionDef = undefined,
}: {
	// eslint-disable-next-line react/require-default-props
	conditionDef?: ConditionDef;
}) {
	if (!conditionDef) return <Text type='secondary'>Condition not found</Text>;

	const c = conditionDef.condition;

	return (
		<div style={{ width: 300 }}>
			<div style={{ marginBottom: 8 }}>
				<div style={{ fontWeight: 600 }}>{conditionDef.name}</div>
				<Text type='secondary' style={{ fontSize: 12 }}>
					{conditionDef.id}
				</Text>
			</div>

			<Descriptions size='small' column={1} bordered>
				<Descriptions.Item label='Type'>{c.type ?? "-"}</Descriptions.Item>

				<Descriptions.Item label='Active'>
					{c.active === true ? <Tag color='green'>true</Tag> : <Tag color='red'>false</Tag>}
				</Descriptions.Item>
				<Descriptions.Item label='Message'>{c.message ?? "-"}</Descriptions.Item>
				<Descriptions.Item label='Front Door'>{c.frontDoor ?? "null"}</Descriptions.Item>
				<Descriptions.Item label='Rear Door'>{c.rearDoor ?? "null"}</Descriptions.Item>
				<Descriptions.Item label='Direction'>{c.direction ?? "null"}</Descriptions.Item>
			</Descriptions>
		</div>
	);
}

export default function IconDefinitionRules({
	adapter,
	isLoading,
	viewOnly,
}: {
	isLoading: boolean;
	adapter: AdapterInterface;
	viewOnly: boolean;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");
	const ICONS: IconsInterface[] = [
		{ id: "elUp", name: "Up", icon: elUp },
		{ id: "elDown", name: "Down", icon: elDown },
		{ id: "elClose", name: "door", icon: elClose },
		{ id: "elOpen", name: "Open", icon: elOpen },
		{ id: "elFrontCloseRearClose", name: "Front Close Rear Close", icon: elFrontCloseRearClose },
		{ id: "elFrontCloseRearOpen", name: "Front Close Rear Open", icon: elFrontCloseRearOpen },
		{ id: "elFrontOpenRearClose", name: "Front Open Rear Close", icon: elFrontOpenRearClose },
		{ id: "elFrontOpenRearOpen", name: "Front Open Rear Open", icon: elFrontOpenRearOpen },
	];

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [editIconId, setEditIconId] = useState<string>("");
	const [selectData, setSelectData] = useState<{ label: string; value: string; icon: string }[]>([]);
	const [selectValues, setSelectValues] = useState<string[]>([]);
	// eslint-disable-next-line no-unused-vars
	const [removeIcon, setRemoveIcon] = useState<string>("");

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	// eslint-disable-next-line no-unused-vars
	const { theme } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { putAdapter, isErrorPutAdapter, isSuccessPutAdapter, isLoadingPutAdapter } = usePutAdapter();

	// ************************************************ */
	// HELPERS **************************************** */

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// USE MEMO *************************************** */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	function addChildRefByConditionId(rules: RuleDefinition[], conditionId: string): RuleDefinition[] {
		return rules.map((rule: any) => {
			const existsInConditions = rule.conditions?.some((c: any) => c.id === conditionId);
			if (!existsInConditions) return rule;
			const children = rule.activeWhen?.children ?? [];
			const alreadyInChildren = children.some((ch: any) => ch?.kind === "ref" && ch?.id === conditionId);
			return {
				...rule,
				activeWhen: {
					...rule.activeWhen,
					children: alreadyInChildren ? children : [...children, { id: conditionId, kind: "ref" }],
				},
				removeWhen: { id: removeIcon === "" ? "" : removeIcon, kind: "ref" },
			};
		});
	}

	function addAndGroupByConditionIds(rules: RuleDefinition[], conditionIds: string[]): RuleDefinition[] {
		if (!Array.isArray(conditionIds) || conditionIds.length < 2) return rules;
		return rules.map((rule: any) => {
			const children = rule.activeWhen?.children ?? [];
			const idsInRule = conditionIds.filter((id) => rule.conditions?.some((c: any) => c.id === id));
			if (idsInRule.length < 2) return rule;
			const uniqueIdsInRule = Array.from(new Set(idsInRule));
			const newAndGroup = {
				kind: "group",
				op: "AND",
				children: uniqueIdsInRule.map((id) => ({ kind: "ref", id })),
			};
			const targetSet = new Set(uniqueIdsInRule);
			const alreadyExists = children.some((ch: any) => {
				if (ch?.kind !== "group" || ch?.op !== "AND" || !Array.isArray(ch?.children)) return false;
				const refs = ch.children
					.filter((x: any) => x?.kind === "ref" && typeof x?.id === "string")
					.map((x: any) => x.id);
				if (refs.length !== targetSet.size) return false;
				const refSet = new Set(refs);
				if (refSet.size !== targetSet.size) return false;
				// eslint-disable-next-line no-restricted-syntax
				for (const id of targetSet) {
					if (!refSet.has(id)) return false;
				}
				return true;
			});
			if (alreadyExists) return rule;
			return {
				...rule,
				activeWhen: {
					...rule.activeWhen,
					children: [...children, newAndGroup],
				},
				removeWhen: { id: removeIcon === "" ? "" : removeIcon, kind: "ref" },
			};
		});
	}

	const onClickSave = () => {
		if (selectValues.length === 1) {
			const updatedRules = addChildRefByConditionId(adapter.socket_message_priority.rules, selectValues[0]);
			putAdapter({
				...adapter,
				socket_message_priority: {
					customConfiguration: adapter?.socket_message_priority?.customConfiguration,
					rules: updatedRules,
				},
			});
		} else {
			const updatedRules = addAndGroupByConditionIds(adapter.socket_message_priority.rules, selectValues);
			putAdapter({
				...adapter,
				socket_message_priority: {
					customConfiguration: adapter?.socket_message_priority?.customConfiguration,
					rules: updatedRules,
				},
			});
		}
	};

	const onChangeSelectActivateIcon = (newValues: string[]) => {
		setSelectValues(newValues);
	};

	const onChangeSelectRemoveIcon = (newValue: string) => {
		setRemoveIcon(newValue);
	};

	const getCondition = (id: string, icon: string, rules: RuleDefinition[]) =>
		rules?.find((r) => r.icon === icon)?.conditions.find((c) => c.id === id);

	const getConditionName = (id: string, icon: string, rules: RuleDefinition[]): string | undefined => {
		if (id !== "N" && icon !== "") {
			return rules?.find((r) => r.icon === icon)?.conditions.find((c) => c.id === id)?.name;
		}
		return "NO REMOVE";
	};

	const onClickDeleteItem = useCallback(
		(ruleExpression: RuleExpr, index: number, icon: string) => {
			const rules = adapter?.socket_message_priority?.rules ?? [];

			const indexRule = rules.findIndex((item: RuleDefinition) => item.icon === icon);
			if (indexRule < 0) return;

			const activeWhen = rules[indexRule].activeWhen;
			if (activeWhen.kind !== "group") return;

			const newChildren = activeWhen.children.filter((_: RuleExpr, i: number) => i !== index);

			const copy: RuleDefinition[] = structuredClone(rules);
			if (copy[indexRule].activeWhen.kind !== "group") return;

			copy[indexRule].activeWhen.children = newChildren;

			putAdapter({
				...adapter,
				socket_message_priority: {
					customConfiguration: adapter?.socket_message_priority?.customConfiguration,
					rules: copy,
				},
			});
		},
		[adapter, putAdapter],
	);

	// ************************************************ */
	// USE CALLBACK *********************************** */
	const onClickEdit = useCallback((icon: IconsInterface) => {
		setEditIconId(icon.id);
	}, []);

	const onClickCancel = useCallback(() => {
		setEditIconId("");
	}, []);

	const renderActiveWhen = useCallback(
		(activeWhen: RuleExpr, conditions: ConditionDef[], icon: string, iconObj: IconsInterface) => {
			const byId = new Map(conditions.map((c) => [c.id, c]));
			const labelFor = (condId: string) => {
				const cond = byId.get(condId);
				return cond?.name ?? condId;
			};
			const renderExpr = (expr: RuleExpr): React.ReactNode => {
				if (expr.kind === "ref") {
					const conditionDef = getCondition(expr.id, icon, adapter.socket_message_priority.rules);
					const label = labelFor(expr.id);
					return (
						<div
							style={{
								padding: "6px 10px",
								borderRadius: 10,
								marginTop: 6,
								width: "100%",
								textAlign: "center",
								cursor: "pointer",
							}}
							className={`generalStyles__container__${theme}`}
						>
							<div style={{ paddingTop: "5px" }}>
								<Tooltip
									title={<ConditionTooltipContent conditionDef={conditionDef} />}
									overlayInnerStyle={{
										width: 315,
										cursor: "pointer",
									}}
									placement='right'
								>
									<img style={{ marginRight: "10px", marginTop: "-3px" }} src={iconObj.icon} alt={iconObj.name} />
									<span>{label}</span>
								</Tooltip>
							</div>
						</div>
					);
				}
				if (expr.op === "OR") {
					return (
						<div>
							{expr.children.map((child, idx) => (
								<div key={idx} className='mt-2'>
									<div className='generalStyles__spaceBetween'>
										<div className='generalStyles__flexCenter'>
											<Tag color='orange'>OR</Tag>
										</div>
										<div>
											{!viewOnly && (
												<Tooltip title='Delete'>
													<Button
														shape='circle'
														className='buttonStyle__29'
														onClick={() => {
															onClickDeleteItem(child, idx, icon);
														}}
														icon={<FontAwesomeIcon style={{ fontSize: "15px" }} icon={faTrash} />}
													/>
												</Tooltip>
											)}
										</div>
									</div>
									<OrBlock key={idx} theme={theme}>
										{renderExpr(child)}
									</OrBlock>
								</div>
							))}
						</div>
					);
				}
				return (
					<AndBox>
						<Tag color='blue'>AND</Tag>
						{expr.children.map((child, idx) => (
							<div key={idx}>{renderExpr(child)}</div>
						))}
					</AndBox>
				);
			};
			return renderExpr(activeWhen);
		},
		[onClickDeleteItem],
	);

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		if (adapter?.socket_message_priority?.rules && adapter.socket_message_priority.rules.length > 0) {
			const rows: { label: string; value: string; icon: string }[] = adapter.socket_message_priority.rules.flatMap(
				(rule: RuleDefinition) =>
					(rule.conditions ?? []).map((c) => ({
						label: c.name,
						value: c.id,
						icon: rule.icon,
					})),
			);
			setSelectData(rows);
		}
	}, [adapter]);

	useEffect(() => {
		if (isErrorPutAdapter || isSuccessPutAdapter) {
			setEditIconId("");
			setSelectData([]);
			setSelectValues([]);
		}
	}, [isErrorPutAdapter, isSuccessPutAdapter]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='generalStyles__flexWrap'>
			{ICONS.map((icon: IconsInterface) => {
				const ruleForIcon = adapter?.socket_message_priority?.rules.find((r) => r.icon === icon.id);
				return (
					<Card
						key={icon.id}
						title={
							<div>
								<img src={icon.icon} alt={icon.name} />
								<span className='generalStyles__mlFix'>{icon.name}</span>
							</div>
						}
						loading={isLoading || isLoadingPutAdapter}
						extra={
							<>
								{editIconId === icon.id ? (
									<>
										<Tooltip title={t("general.save")}>
											<Button
												shape='circle'
												className='buttonStyle__10'
												onClick={() => onClickSave()}
												icon={<FontAwesomeIcon icon={faSave} />}
											/>
										</Tooltip>
										<Tooltip title={t("general.cancel")}>
											<Button
												shape='circle'
												className='buttonStyle__25 generalStyles__mlFix'
												onClick={() => onClickCancel()}
												icon={<FontAwesomeIcon icon={faXmark} />}
											/>
										</Tooltip>
									</>
								) : (
									<>
										{!viewOnly && (
											<Tooltip title={t("general.edit")}>
												<Button
													shape='circle'
													className='buttonStyle__11'
													onClick={() => onClickEdit(icon)}
													icon={<FontAwesomeIcon icon={faPencil} />}
												/>
											</Tooltip>
										)}
									</>
								)}
							</>
						}
						style={{ width: 360, margin: "5px" }}
					>
						{editIconId === icon.id ? (
							<div>
								<Divider className='generalStyles__info'>{t("general.activateIconWhen")}</Divider>
								<Select
									onChange={onChangeSelectActivateIcon}
									mode='multiple'
									style={{ width: "100%" }}
									placeholder='Please select'
									options={selectData.filter((option) => option.icon === icon.id)}
								/>
								{/* <Divider className='generalStyles__error'>{t("general.removeIconWhen")}</Divider>
								<Select
									onChange={onChangeSelectRemoveIcon}
									mode='multiple'
									style={{ width: "100%" }}
									placeholder='Please select'
									options={selectData.filter((option) => option.icon === icon.id)}
								/> */}
							</div>
						) : (
							<div>
								{ruleForIcon && ruleForIcon?.activeWhen?.children?.length >= 1 ? (
									<div style={{ marginTop: 6 }}>
										{" "}
										<Divider className='generalStyles__info'>{t("general.activateIconWhen")}</Divider>
										{renderActiveWhen(ruleForIcon.activeWhen, ruleForIcon.conditions, ruleForIcon.icon, icon)}
										<Divider className='generalStyles__error'>{t("general.removeIconWhen")}</Divider>
										<div className='mt-2'>
											<OrBlock theme={theme}>
												<div
													style={{
														padding: "6px 10px",
														borderRadius: 10,
														marginTop: 6,
														width: "100%",
														gap: 8,
														textAlign: "center",
														cursor: "pointer",
													}}
													className={`generalStyles__container__${theme}`}
												>
													<div style={{ paddingTop: "5px" }}>
														<Tooltip
															title={
																<ConditionTooltipContent
																	conditionDef={getCondition(
																		ruleForIcon?.removeWhen?.id[0] || "",
																		ruleForIcon?.icon,
																		adapter.socket_message_priority.rules,
																	)}
																/>
															}
															overlayInnerStyle={{
																width: 315,
																cursor: "pointer",
															}}
															placement='right'
														>
															<span>
																{getConditionName(
																	ruleForIcon?.removeWhen?.id[0] || "",
																	ruleForIcon?.icon,
																	adapter.socket_message_priority.rules,
																)}
															</span>
														</Tooltip>
													</div>
												</div>
											</OrBlock>
										</div>
									</div>
								) : (
									<div style={{ opacity: 0.7, textAlign: "center" }}>No rule configured for this icon.</div>
								)}
							</div>
						)}
					</Card>
				);
			})}
		</div>
	);
}
